/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    AsyncResult,
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { ProductList, ProductListSearchCriteria, ProductListType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    OrderTemplate,
    QueryResultSettingsProxy,
    GetPaginatedOrderTemplateLinesInput,
    getPaginatedOrderTemplateLinesAction
} from '@msdyn365-commerce-modules/retail-actions';

import { IOrderTemplateLinesState } from '@msdyn365-commerce-modules/retail-actions/src/utilities/order-templates';
import { GetCatalogIdFromContext } from '../utilities';
import { getCatalogProductListAsync } from './DataActionExtension.g';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetOrderTemplatesPreviewInput implements IActionInput {
    public queryResultSettings: IQueryResultSettings;

    constructor(queryResultSettings: IQueryResultSettings) {
        this.queryResultSettings = queryResultSettings;
        this.queryResultSettings.count = true;
        this.queryResultSettings.Paging!.Top = 4;
    }

    public getCacheKey = () => 'GetOrderTemplatesPreview';

    public getCacheObjectType = () => 'GetOrderTemplatesPreview';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 */
export const createGetOrderTemplatesPreviewInput = (inputData: ICreateActionContext): GetOrderTemplatesPreviewInput => {
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    return new GetOrderTemplatesPreviewInput(queryResultSettings);
};

/**
 * The action method for the GetOrderTemplatesPreview data action.
 * @param input The action input.
 * @param context The action context.
 */
export async function getOrderTemplatesPreview(input: GetOrderTemplatesPreviewInput, context: IActionContext): Promise<OrderTemplate[]> {
    try {
        const productListSearchCriteria: ProductListSearchCriteria = {
            ProductListId: '',
            ProductListType: `${ProductListType.OrderTemplate}`
        };
        const catalogId = GetCatalogIdFromContext(context);
        const queryResultSettings = QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context);
        const resp = await getCatalogProductListAsync(
            {
                callerContext: context,
                queryResultSettings: queryResultSettings
            },
            +catalogId || 0,
            productListSearchCriteria
        );

        const orderTemplates: OrderTemplate[] = [];
        const inputLinesRequests: AsyncResult<IOrderTemplateLinesState>[] = [];
        const productLists = resp as ProductList[];
        for (const list of productLists) {
            const inputLine = new GetPaginatedOrderTemplateLinesInput(list.Id, input.queryResultSettings, undefined);
            inputLinesRequests.push(getPaginatedOrderTemplateLinesAction(inputLine, context));
        }
        const inputLinesResponses = await Promise.all(inputLinesRequests);

        for (const list of productLists) {
            const productLinesResponse = inputLinesResponses.filter(
                x => x.lines.length > 0 && x.lines[0].productListLine.ProductListId === list.Id
            );

            if (productLinesResponse.length > 0) {
                orderTemplates.push(new OrderTemplate(list, productLinesResponse[0].lines, productLinesResponse[0].totalCount));
            } else {
                orderTemplates.push(new OrderTemplate(list, [], 0));
            }
        }
        return orderTemplates;
    } catch (error) {
        context.telemetry.error('Not able to get order template(s)', error);
        throw error;
    }
}

/**
 * The GetOrderTemplatesPreview Data Action
 * Returns order templates.
 */
export const getOrderTemplatesPreviewDataAction = createObservableDataAction<OrderTemplate[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-order-templates-preview',
    action: <IAction<OrderTemplate[]>>getOrderTemplatesPreview,
    input: createGetOrderTemplatesPreviewInput
});

export default getOrderTemplatesPreviewDataAction;
